import React, { useEffect, useMemo, useState } from "react";
import QuarryTableView from "./QuarryTableView";
import { ViewListingTypes } from "../../enums/ViewListingTypes";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import Form from "../Form";
import SummaryHeader from "./SummaryHeader";
import UtilityBar from "./UtilityBar";
import FilterDrawer from "./FilterDrawer";
import AddQuarryDrawer from "./AddQuarryDrawer";
import QuarryDatabaseService from "../../services/quarryDatabase.service";
import { ModalModes } from "../../enums/DatabaseEnums";
import { convertToFormValuesAndDaySwitchVals, FILTER_FIELD_NAMES_TO_COUNT } from "./constants";
import TNLoader from "../../reusable-components/TNLoader";
import QuarryMapView from "./QuarryMapView";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { useQuarryDatabaseContext } from "../../context/QuarryDatabaseFilterContext";
import { countFiltersApplied } from "../../utils/countAppliedFilters";

const QuarryDatabaseList = () => {
  const navigate = useHistory();
  const location = useLocation();
  const { quarryDatabaseFilters, updateQuarryDatabaseFilters } =
    useQuarryDatabaseContext();
  const { MAP, LIST } = ViewListingTypes;

  const getActiveKeyFromHash = () => {
    const hash = location.hash.replace("#", "");
    const isValidKey = Object.values(ViewListingTypes)?.some(
      (tab) => tab === hash,
    );
    return isValidKey ? (hash as ViewListingTypes) : ViewListingTypes.LIST;
  };

  const [lastCreatedQuarryId, setLastCreatedQuarryId] = useState<number>(0);

  const handleUpdateLastCreatedQuarryId = (lastCreated: number) => {
    setLastCreatedQuarryId(lastCreated);
  };

  const [activeView, setActiveView] =
    useState<ViewListingTypes>(getActiveKeyFromHash);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState<boolean>(false);
  const [quarryId, setQuarryId] = useState<string>("");
  const [quarryModalMode, setQuarryModalMode] = useState<ModalModes>(
    ModalModes.CREATE,
  );
  const handleFilterDrawerOpen = () => setIsFilterDrawerOpen(true);
  const handleFilterDrawerClose = () => setIsFilterDrawerOpen(false);
  const handleAddDrawerOpen = () => setIsAddDrawerOpen(true);
  const handleAddDrawerClose = () => {
    setQuarryId("");
    setQuarryModalMode(ModalModes.CREATE);
    setIsAddDrawerOpen(false);
  };
  const { AddNewQuarry, FetchQuarryDetails, EditQuarry } =
    QuarryDatabaseService();

  const { mutateAsync: addNewQuarry, isPending: isAddCallPending } =
    AddNewQuarry();
  const { mutateAsync: editQuarry, isPending: isEditCallPending } =
    EditQuarry();
  const {
    data,
    refetch,
    isFetching: isDetailsFetching,
  } = FetchQuarryDetails(quarryId);
  const handleEditNavigate = (id?: string) => {
    setQuarryId(id);
    setQuarryModalMode(ModalModes.EDIT);
    handleAddDrawerOpen();
  };
  const handleViewNavigate = (record?: object) => {
    const locationProp = {
      quarryName: record?.quarryName,
    };
    navigate.push(
      generatePath(appRoutes.QUARRY_PROFILE_ID, {
        id: record?.id,
      }),
      locationProp,
    );
  };

  const handleViewToggle = (selectedView: ViewListingTypes) => {
    setActiveView(selectedView);
    window.location.hash = selectedView;
  };

  const handleClearAllFilter = () => {
    updateQuarryDatabaseFilters({
      latitude: undefined,
      longitude: undefined,
      category: undefined,
      materialType: undefined,
      radius: undefined,
      address: undefined,
    });
  };

  const filterCount = countFiltersApplied(
    quarryDatabaseFilters,
    FILTER_FIELD_NAMES_TO_COUNT,
  );

  const formData = data ? convertToFormValuesAndDaySwitchVals(data) : {};

  const tableFilter = useMemo(
    () => ({
      ...quarryDatabaseFilters,
    }),
    [quarryDatabaseFilters],
  );

  useEffect(() => {
    refetch();
  }, [quarryId]);

  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = ViewListingTypes.LIST;
    }
  }, []);

  return (
    <>
      <TNLoader
        isLoading={isDetailsFetching || isAddCallPending || isEditCallPending}
      />
      <SummaryHeader
        filters={tableFilter}
        countCallback={handleUpdateLastCreatedQuarryId}
      />
      <Form>
        {() => (
          <UtilityBar
            handleFilterDrawerOpen={handleFilterDrawerOpen}
            handleAddDrawerOpen={handleAddDrawerOpen}
            activeView={activeView}
            handleViewToggle={handleViewToggle}
            filterCount={filterCount}
            handleClearAllFilter={handleClearAllFilter}
          />
        )}
      </Form>
      <Form
        defaultValues={{
          materialType: quarryDatabaseFilters?.materialType ?? [],
          category: quarryDatabaseFilters?.category ?? [],
          addressField: !!quarryDatabaseFilters?.latitude
            ? quarryDatabaseFilters?.address
            : undefined,
          radius: quarryDatabaseFilters?.radius ?? undefined,
        }}
      >
        {() => (
          <FilterDrawer
            visible={isFilterDrawerOpen}
            onClose={handleFilterDrawerClose}
            handleClearAllFilter={handleClearAllFilter}
          />
        )}
      </Form>
      <Form
        values={
          quarryModalMode === ModalModes.CREATE
            ? { quarryId: String(lastCreatedQuarryId) }
            : formData?.formValues
        }
        key={
          quarryModalMode === ModalModes.CREATE
            ? ModalModes.CREATE
            : JSON.stringify(formData?.formValues)
        }
      >
        {({ control: { _reset }, reset }) => (
          <AddQuarryDrawer
            isOpen={isAddDrawerOpen}
            handleCloseDrawer={() => {
              handleAddDrawerClose();
              reset();
              _reset();
            }}
            submitAction={
              quarryModalMode === ModalModes.CREATE ? addNewQuarry : editQuarry
            }
            submitStatus={
              quarryModalMode === ModalModes.CREATE
                ? isAddCallPending
                : isEditCallPending
            }
            quarryModalMode={quarryModalMode}
            setQuarryModalMode={setQuarryModalMode}
            quarryId={quarryId}
            profileView={false}
            handleViewNavigate={handleViewNavigate}
          />
        )}
      </Form>
      <ConditionalRender visible={activeView === LIST}>
        <QuarryTableView
          filter={tableFilter}
          handleEditNavigate={handleEditNavigate}
          handleViewNavigate={handleViewNavigate}
        />
      </ConditionalRender>
      <ConditionalRender visible={activeView === MAP}>
        <QuarryMapView
          filter={tableFilter}
          handleEditNavigate={handleEditNavigate}
          handleViewNavigate={handleViewNavigate}
          isDrawerOpen={isAddDrawerOpen}
        />
      </ConditionalRender>
    </>
  );
};

export default QuarryDatabaseList;
